import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CalendarClock, RefreshCw, Settings, Mic, Stethoscope, Heart } from "lucide-react";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";

const CalendlyDiagnostic = ({ diag }) => (
  <div className="mb-4 rounded-lg border bg-muted/30 p-3 text-xs space-y-2" data-testid="calendly-diagnostic">
    <p><span className="font-semibold">Compte Calendly :</span> {diag.user?.name} ({diag.user?.email}) · {diag.events_user_scope} RDV lus depuis 2025{diag.events_org_scope ? ` · ${diag.events_org_scope} au niveau de l'organisation` : ""}</p>
    <div>
      <p className="font-semibold">Types d'événements</p>
      <ul className="list-disc pl-4">
        {diag.event_types.map((t, i) => <li key={i}>{t.name} {t.couple_detected && <span className="text-rose-600">· couple détecté</span>} {t.active === false && <span className="text-muted-foreground">· inactif</span>} {t.pooling_type && <span className="text-muted-foreground">· {t.pooling_type}</span>}</li>)}
      </ul>
    </div>
    <div>
      <p className="font-semibold">RDV lus par nom</p>
      <ul className="list-disc pl-4">{Object.entries(diag.by_name).map(([n, c]) => <li key={n}>{n} : {c}</li>)}</ul>
      {Object.keys(diag.org_only_by_name || {}).length > 0 && <p className="text-amber-700 mt-1">RDV visibles uniquement au niveau organisation (autre hôte) : {Object.entries(diag.org_only_by_name).map(([n, c]) => `${n} (${c})`).join(", ")}</p>}
    </div>
    {diag.samples?.length > 0 && (
      <div>
        <p className="font-semibold">Derniers RDV analysés</p>
        {diag.samples.map((s, i) => (
          <div key={i} className="mt-1 rounded border bg-background p-2">
            <p>{s.name} · {formatDateSafe(s.start_time)} {s.couple_detected && <span className="text-rose-600">· couple</span>} · invités HTTP {s.invitees_status}</p>
            <p>Invités : {s.invitees.map((v) => `${v.name || "?"} <${v.email}> (${v.status})`).join(" ; ") || "aucun"}</p>
            {s.guests?.length > 0 && <p>Invités supplémentaires : {s.guests.join(", ")}</p>}
            {s.invitees.some((v) => v.questions?.length) && <p>Questions : {s.invitees.flatMap((v) => v.questions.map((q) => `${q.q} → ${q.a}`)).join(" ; ")}</p>}
            {s.participants_detected && <p className="text-emerald-700">Participants détectés : {s.participants_detected.map((p) => `${p.name} <${p.email}>`).join(" ; ")}</p>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const UpcomingAppointments = () => {
  const [data, setData] = useState({ appointments: [], last_sync: null, calendly_configured: false });
  const [syncing, setSyncing] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiag = async () => {
    if (diag) { setDiag(null); return; }
    setDiagBusy(true);
    try { const r = await axios.get(`${API}/calendly/diagnostic`, { withCredentials: true }); setDiag(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur diagnostic"); }
    finally { setDiagBusy(false); }
  };

  const load = () => axios.get(`${API}/appointments`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { window.addEventListener("kintsugi:calendly-synced", load); return () => window.removeEventListener("kintsugi:calendly-synced", load); }, []);
  useEffect(() => { load(); }, []);

  const [syncProgress, setSyncProgress] = useState(null);
  const sync = async () => {
    setSyncing(true); setSyncProgress(null);
    try {
      const start = await axios.post(`${API}/calendly/sync`, {}, { withCredentials: true });
      let s = null;
      for (let i = 0; i < 400; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const j = (await axios.get(`${API}/calendly/sync/${start.data.job_id}`, { withCredentials: true })).data;
        if (j.progress?.total) setSyncProgress(j.progress);
        if (j.status === "completed") { s = j.result; break; }
        if (j.status === "error") throw new Error(j.error || "Erreur de synchronisation");
      }
      if (!s) throw new Error("La synchronisation prend trop de temps, réessayez plus tard.");
      toast.success(`Calendly : ${s.events} RDV lus (${s.couple_events || 0} de couple) · ${s.patients_created} patient(s) créé(s) · ${s.reports_created} CR créé(s) · ${s.appointments} RDV à venir${s.already_synced ? ` · ${s.already_synced} déjà synchronisés` : ""}${s.invitee_errors ? ` · ${s.invitee_errors} RDV sans invités lisibles` : ""}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur de synchronisation"); }
    finally { setSyncing(false); setSyncProgress(null); }
  };

  return (
    <Card data-testid="upcoming-appointments-card">
      <CardHeader className="p-4 sm:p-6 flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Rendez-vous à venir</CardTitle>
          {data.last_sync && <p className="text-xs text-muted-foreground mt-1" data-testid="calendly-last-sync">Dernière synchro : {formatDateSafe(data.last_sync)}</p>}
        </div>
        {data.calendly_configured ? (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={runDiag} disabled={diagBusy} className="gap-1.5" title="Diagnostic Calendly : types d'événements et RDV lus" data-testid="calendly-diag-btn">
              <Stethoscope className={`w-4 h-4 ${diagBusy ? "animate-pulse" : ""}`} />
            </Button>
            <Button size="sm" variant="outline" onClick={sync} disabled={syncing} className="gap-1.5" data-testid="calendly-sync-btn">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? (syncProgress ? `Synchro ${syncProgress.done}/${syncProgress.total}…` : "Synchro...") : "Synchroniser Calendly"}
            </Button>
          </div>
        ) : (
          <Link to="/profile#messaging"><Button size="sm" variant="outline" className="gap-1.5" data-testid="calendly-configure-link"><Settings className="w-4 h-4" /> Connecter Calendly</Button></Link>
        )}
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        {diag && <CalendlyDiagnostic diag={diag} />}
        {data.appointments.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-appointments">
            {data.calendly_configured ? "Aucun rendez-vous à venir. Lancez une synchronisation." : "Ajoutez votre jeton Calendly dans Mon profil → Messagerie & IA pour importer automatiquement vos rendez-vous."}
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {data.appointments.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`appointment-${a.id}`}>
                <Link to={`/therapist/patients/${a.patient_id}`} className="min-w-0 flex-1">
                  <p className="font-medium truncate flex items-center gap-1.5">{a.couple && <Heart className="w-3.5 h-3.5 text-rose-500 shrink-0" />}{a.patient_name || "Patient"}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.event_name}{a.location ? ` · ${a.location}` : ""}</p>
                </Link>
                <span className="text-sm text-primary whitespace-nowrap">{formatDateSafe(a.start_time, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <Link to={`/therapist/session-reports?patient=${a.patient_id}&type=${a.couple ? "therapie_couple" : "suivi_tcc"}&record=1`} title="Démarrer la séance : enregistrement + transcription horodatée">
                  <Button size="sm" className="gap-1.5 shrink-0" data-testid={`start-session-${a.id}`}><Mic className="w-4 h-4" /><span className="hidden sm:inline">Démarrer la séance</span></Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingAppointments;
